import React from 'react';
import { Flex } from 'antd';
import { faChartSimple } from '@fortawesome/pro-thin-svg-icons';
import { keyBy } from 'lodash-es';

import { StudyClient, StudyLibraryClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExCard } from 'jf/components/DevExCard';
import { DevExEmpty } from 'jf/components/DevExEmpty';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExSkeleton } from 'jf/components/DevExSkeleton';
import { DevExTag } from 'jf/components/DevExTag';
import { DevExText } from 'jf/components/DevExText';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { PromptScoreCard, PromptTextResponseCard } from '../topicDrawer/PromptScoreCard';
import { useSelectPrompt } from '../topicDrawer/useSelectPrompt';
import { useTextResponses } from '../topicDrawer/useTextResponses';
import { useCanViewSurveyResults } from '../useCanViewSurveyResults';
import { useStudyScores } from '../useStudyScores';

const HISTOGRAM_HEIGHT = 270;

type PromptResultsProps = {
  studyRef: string | undefined;
  teamRef?: string;
  promptSlug: string | undefined;
};

export const PromptResults: React.FC<PromptResultsProps> = (props) => {
  const theme = useDevExTheme();

  const { data: study, isLoading: studyIsLoading } = useClientQuery(StudyClient.getStudy, {
    studyRef: props.studyRef,
  });
  const { data: topics, isLoading: topicsAreLoading } = useClientQuery(
    StudyLibraryClient.getTopics
  );
  const { data: promptScores, isLoading: promptScoresAreLoading } = useStudyScores({
    studyRef: props.studyRef,
    teamRef: props.teamRef,
    type: 'PROMPT',
  });
  const { data: textResponses, isLoading: textResponsesAreLoading } = useTextResponses({
    studyRef: props.studyRef,
    teamRef: props.teamRef,
  });

  const promptScore = promptScores?.find((score) => score.slug === props.promptSlug);
  const textResponse = textResponses?.filter(
    (textResponse) => textResponse.prompt === props.promptSlug
  );
  const prompt = useSelectPrompt({ promptSlug: props.promptSlug! });

  const topicsBySlug = keyBy(topics, 'slug');

  const topic = prompt && topicsBySlug[prompt.topicSlug];

  const promptIndex = study?.prompts.findIndex((prompt) => prompt.slug === props.promptSlug) ?? 0;

  const shouldShowSurveyResults = useCanViewSurveyResults({ studyRef: props.studyRef });

  const isLoading =
    studyIsLoading || topicsAreLoading || promptScoresAreLoading || textResponsesAreLoading;

  return (
    <Flex
      vertical
      gap={theme.variable.spacing.md}
      style={{ flex: 1, paddingTop: theme.variable.spacing.sm }}
    >
      <Flex align="center" justify="space-between" style={{ height: 24 }}>
        <DevExText type="secondary" loading={!prompt || !study} loadingWidth={120}>
          Question {promptIndex + 1} / {study?.prompts.length}
        </DevExText>

        {prompt ? (
          <Flex justify="end" gap={16}>
            {!!topic && <DevExTag>{topic.label}</DevExTag>}
          </Flex>
        ) : (
          <DevExSkeleton width={120} height={24} />
        )}
      </Flex>

      {isLoading ? (
        <DevExLoader />
      ) : shouldShowSurveyResults && prompt && promptScores && promptScore ? (
        <PromptScoreCard score={promptScore} promptSlug={prompt.slug} />
      ) : shouldShowSurveyResults && prompt && textResponses && textResponse?.length ? (
        <PromptTextResponseCard
          responses={textResponse}
          promptSlug={prompt.slug}
          studyRef={props.studyRef}
          topicSlug={prompt.topicSlug}
        />
      ) : (
        <DevExCard
          style={{ height: HISTOGRAM_HEIGHT }}
          bodyStyle={{ justifyContent: 'center', height: '100%' }}
        >
          <DevExEmpty
            icon={faChartSimple}
            label="Your results will be available after the survey is closed"
          />
        </DevExCard>
      )}
    </Flex>
  );
};
